import { DeepMap } from '@cld/common-definitions';
import { BlendIntensity, BlendMode, BlendVariant } from './BlenderProps';
import { BackgroundBlendOptions, ColorName, ForegroundBlendOptions } from './blendOptions';

const map = (baseColor: ColorName, blendColor: ColorName, blendOpacity: BackgroundBlendOptions['blendOpacity']): BackgroundBlendOptions => ({
  baseColor,
  blendColor,
  blendOpacity,
});

const plainBackground = {
  primary: {
    sm: map('transparent', 'primary', 0.12),
    md: map('transparent', 'primary', 0),
    lg: map('transparent', 'primary', 0.06),
    xl: map('transparent', 'primary', 0.18),
  },
  secondary: {
    sm: map('transparent', 'contrastHigh', 0.12),
    md: map('transparent', 'contrastHigh', 0),
    lg: map('transparent', 'contrastHigh', 0.06),
    xl: map('transparent', 'contrastHigh', 0.18),
  },
  error: {
    sm: map('transparent', 'error', 0.12),
    md: map('transparent', 'error', 0),
    lg: map('transparent', 'error', 0.06),
    xl: map('transparent', 'error', 0.18),
  },
  success: {
    sm: map('transparent', 'success', 0.12),
    md: map('transparent', 'success', 0),
    lg: map('transparent', 'success', 0.06),
    xl: map('transparent', 'success', 0.18),
  },
  warning: {
    sm: map('transparent', 'warning', 0.12),
    md: map('transparent', 'warning', 0),
    lg: map('transparent', 'warning', 0.06),
    xl: map('transparent', 'warning', 0.18),
  },
  overlay: {
    sm: map('transparent', 'overlayLight', 0.12),
    md: map('transparent', 'overlayDark', 0),
    lg: map('transparent', 'overlayDark', 0.06),
    xl: map('transparent', 'overlayDark', 0.18),
  },
};
const surfaceBackground = {
  primary: {
    sm: map('surface', 'primary', 0.12),
    md: map('surface', 'primary', 0),
    lg: map('surface', 'primary', 0.06),
    xl: map('surface', 'primary', 0.18),
  },
  secondary: {
    sm: map('surface', 'contrastHigh', 0.12),
    md: map('surface', 'contrastHigh', 0),
    lg: map('surface', 'contrastHigh', 0.06),
    xl: map('surface', 'contrastHigh', 0.18),
  },
  error: {
    sm: map('surface', 'error', 0.12),
    md: map('surface', 'error', 0),
    lg: map('surface', 'error', 0.06),
    xl: map('surface', 'error', 0.18),
  },
  success: {
    sm: map('surface', 'success', 0.12),
    md: map('surface', 'success', 0),
    lg: map('surface', 'success', 0.06),
    xl: map('surface', 'success', 0.18),
  },
  warning: {
    sm: map('surface', 'warning', 0.12),
    md: map('surface', 'warning', 0),
    lg: map('surface', 'warning', 0.06),
    xl: map('surface', 'warning', 0.18),
  },
  overlay: {
    sm: map('overlayDark3', 'overlayLight', 0.12),
    md: map('overlayDark3', 'overlayDark', 0),
    lg: map('overlayDark3', 'overlayDark', 0.06),
    xl: map('overlayDark3', 'overlayDark', 0.18),
  },
};

export const backgroundMapping: DeepMap<[BlendMode, BlendVariant, BlendIntensity], BackgroundBlendOptions> = {
  solidContrast: {
    primary: {
      sm: map('primary', 'contrastInvert', 0.12),
      md: map('primary', 'contrastInvert', 0),
      lg: map('primary', 'contrastHigh', 0.06),
      xl: map('primary', 'contrastHigh', 0.18),
    },
    secondary: {
      sm: map('secondary', 'contrastInvert', 0.12),
      md: map('secondary', 'contrastInvert', 0),
      lg: map('secondary', 'contrastHigh', 0.06),
      xl: map('secondary', 'contrastHigh', 0.18),
    },
    error: {
      sm: map('error', 'contrastInvert', 0.12),
      md: map('error', 'contrastInvert', 0),
      lg: map('error', 'contrastHigh', 0.06),
      xl: map('error', 'contrastHigh', 0.18),
    },
    warning: {
      sm: map('warning', 'contrastInvert', 0.12),
      md: map('warning', 'contrastInvert', 0),
      lg: map('warning', 'contrastHigh', 0.06),
      xl: map('warning', 'contrastHigh', 0.18),
    },
    success: {
      sm: map('success', 'contrastInvert', 0.12),
      md: map('success', 'contrastInvert', 0),
      lg: map('success', 'contrastHigh', 0.06),
      xl: map('success', 'contrastHigh', 0.18),
    },
    overlay: {
      sm: map('overlayDark2', 'overlayLight', 0.12),
      md: map('overlayDark2', 'overlayDark', 0),
      lg: map('overlayDark2', 'overlayDark', 0.06),
      xl: map('overlayDark2', 'overlayDark', 0.18),
    },
  },
  plainInvert: {
    primary: {
      sm: map('transparent', 'contrastInvert', 0.12),
      md: map('transparent', 'contrastInvert', 0),
      lg: map('transparent', 'contrastHigh', 0.06),
      xl: map('transparent', 'contrastHigh', 0.18),
    },
    secondary: {
      sm: map('transparent', 'contrastInvert', 0.12),
      md: map('transparent', 'contrastInvert', 0),
      lg: map('transparent', 'contrastHigh', 0.06),
      xl: map('transparent', 'contrastHigh', 0.18),
    },
    error: {
      sm: map('transparent', 'contrastInvert', 0.12),
      md: map('transparent', 'contrastInvert', 0),
      lg: map('transparent', 'contrastHigh', 0.06),
      xl: map('transparent', 'contrastHigh', 0.18),
    },
    warning: {
      sm: map('transparent', 'contrastInvert', 0.12),
      md: map('transparent', 'contrastInvert', 0),
      lg: map('transparent', 'contrastHigh', 0.06),
      xl: map('transparent', 'contrastHigh', 0.18),
    },
    success: {
      sm: map('transparent', 'contrastInvert', 0.12),
      md: map('transparent', 'contrastInvert', 0),
      lg: map('transparent', 'contrastHigh', 0.06),
      xl: map('transparent', 'contrastHigh', 0.18),
    },
    overlay: {
      sm: map('transparent', 'overlayLight', 0.12),
      md: map('transparent', 'overlayDark', 0),
      lg: map('transparent', 'overlayDark', 0.06),
      xl: map('transparent', 'overlayDark', 0.18),
    },
  },
  plainContrast: {
    ...plainBackground,
    overlay: {
      sm: map('transparent', 'contrastInvert', 0.12),
      md: map('transparent', 'contrastInvert', 0),
      lg: map('transparent', 'contrastInvert', 0.06),
      xl: map('transparent', 'contrastInvert', 0.18),
    },
  },
  plainSolid: plainBackground,
  surfaceSolid: surfaceBackground,
  surfaceContrast: {
    ...surfaceBackground,
    overlay: {
      sm: map('overlayLight3', 'overlayDark', 0.12),
      md: map('overlayLight3', 'overlayLight', 0),
      lg: map('overlayLight3', 'overlayLight', 0.06),
      xl: map('overlayLight3', 'overlayLight', 0.18),
    },
  },
};

export const foregroundMapping: DeepMap<[BlendMode, BlendVariant], ForegroundBlendOptions> = {
  solidContrast: {
    primary: {
      colorName: 'contrastInvert',
      typography: 'contrastInvert',
    },
    secondary: {
      colorName: 'contrastHigh',
      typography: 'regular',
    },
    error: {
      colorName: 'contrastInvert',
      typography: 'contrastInvert',
    },
    warning: {
      colorName: 'contrastInvert',
      typography: 'contrastInvert',
    },
    success: {
      colorName: 'contrastInvert',
      typography: 'contrastInvert',
    },
    overlay: {
      colorName: 'overlayLight',
      typography: 'overlay',
    },
  },
  plainInvert: {
    primary: {
      colorName: 'contrastInvert',
      typography: 'contrastInvert',
    },
    secondary: {
      colorName: 'contrastHigh',
      typography: 'regular',
    },
    error: {
      colorName: 'contrastInvert',
      typography: 'contrastInvert',
    },
    warning: {
      colorName: 'contrastInvert',
      typography: 'contrastInvert',
    },
    success: {
      colorName: 'contrastInvert',
      typography: 'contrastInvert',
    },
    overlay: {
      colorName: 'overlayLight',
      typography: 'overlay',
    },
  },
  plainSolid: {
    primary: {
      colorName: 'primary',
      typography: 'url',
    },
    secondary: {
      colorName: 'contrastHigh',
      typography: 'regular',
    },
    error: {
      colorName: 'errorAlt',
      typography: 'error',
    },
    warning: {
      colorName: 'warningAlt',
      typography: 'warning',
    },
    success: {
      colorName: 'successAlt',
      typography: 'success',
    },

    overlay: {
      colorName: 'overlayLight',
      typography: 'overlay',
    },
  },
  plainContrast: {
    primary: {
      colorName: 'contrastHigh',
      typography: 'regular',
    },
    secondary: {
      colorName: 'contrastLow',
      typography: 'lowContrast',
    },
    error: {
      colorName: 'contrastHigh',
      typography: 'regular',
    },
    warning: {
      colorName: 'contrastHigh',
      typography: 'regular',
    },
    success: {
      colorName: 'contrastHigh',
      typography: 'regular',
    },
    overlay: {
      colorName: 'overlayDark',
      typography: 'overlayInvert',
    },
  },
  surfaceSolid: {
    primary: {
      colorName: 'primary',
      typography: 'url',
    },
    secondary: {
      colorName: 'contrastHigh',
      typography: 'regular',
    },
    error: {
      colorName: 'errorAlt',
      typography: 'error',
    },
    warning: {
      colorName: 'warningAlt',
      typography: 'warning',
    },
    success: {
      colorName: 'successAlt',
      typography: 'success',
    },
    overlay: {
      colorName: 'overlayLight',
      typography: 'overlay',
    },
  },
  surfaceContrast: {
    primary: {
      colorName: 'contrastHigh',
      typography: 'regular',
    },
    secondary: {
      colorName: 'contrastLow',
      typography: 'lowContrast',
    },
    error: {
      colorName: 'contrastHigh',
      typography: 'regular',
    },
    warning: {
      colorName: 'contrastHigh',
      typography: 'regular',
    },
    success: {
      colorName: 'contrastHigh',
      typography: 'regular',
    },
    overlay: {
      colorName: 'overlayDark',
      typography: 'overlayInvert',
    },
  },
};
